import { useEffect, useState } from "react";
import { AddTxSheet, type TxType } from "./components/AddTxSheet.tsx";
import { SkeletonCard } from "./components/EmptyState.tsx";
import { StatusBar } from "./components/StatusBar.tsx";
import { ToastHost } from "./components/Toast.tsx";
import { getLang, setLang, t, type Lang } from "./i18n.ts";
import { AdminPage } from "./pages/AdminPage.tsx";
import { DebtsPage } from "./pages/DebtsPage.tsx";
import { HomePage } from "./pages/HomePage.tsx";
import { ListPage } from "./pages/ListPage.tsx";
import { ListsPage } from "./pages/ListsPage.tsx";
import { MorePage } from "./pages/MorePage.tsx";
import { OperationsPage } from "./pages/OperationsPage.tsx";
import { PocketPage } from "./pages/PocketPage.tsx";
import { ReportPage } from "./pages/ReportPage.tsx";
import { useSync } from "./store.ts";
import { hapticSelect, initialScreen, pushBackHandler } from "./telegram.ts";

/** Откуда открыт вложенный экран — туда и вернёт «Назад». */
type From = "home" | "more";

export type Route =
  | { name: "home" }
  | { name: "ops"; month?: string }
  | { name: "lists" }
  | { name: "list"; id: string }
  | { name: "more"; open?: "fridge" | "menu" | "kit" | "warranties" | "accounts" }
  | { name: "admin" }
  | { name: "debts"; from: From; viaReport?: boolean }
  | { name: "report"; from: From };

type TabName = "home" | "ops" | "lists" | "more";

/** Раздел из кнопки бота: ?screen=home|ops|lists|more|fridge|menu|kit|warranties|accounts|admin|debts|report|add. */
function routeFromScreen(screen: string | null): Route {
  switch (screen) {
    case "ops":
      return { name: "ops" };
    case "lists":
      return { name: "lists" };
    case "more":
      return { name: "more" };
    case "fridge":
      return { name: "more", open: "fridge" };
    case "menu":
      return { name: "more", open: "menu" };
    case "kit":
      return { name: "more", open: "kit" };
    case "warranties":
      return { name: "more", open: "warranties" };
    case "accounts":
      return { name: "more", open: "accounts" };
    case "admin":
      return { name: "admin" };
    case "debts":
      return { name: "debts", from: "more" };
    case "report":
      return { name: "report", from: "home" };
    default:
      return { name: "home" };
  }
}

export function App() {
  const { me, status } = useSync();
  const [route, setRoute] = useState<Route>(() => routeFromScreen(initialScreen()));
  const [lang, setLangState] = useState(getLang());
  const [adding, setAdding] = useState<TxType | null>(() => (initialScreen() === "add" ? "expense" : null));
  const isChild = me?.role === "child";

  // Язык из профиля пользователя (его можно сменить и в боте, и в «Ещё»).
  useEffect(() => {
    if (me) {
      setLang(me.language);
      setLangState(me.language);
    }
  }, [me?.language]);

  useEffect(() => {
    document.title = t("appTitle");
  }, [lang]);

  const changeLang = (next: Lang) => {
    setLang(next);
    setLangState(next);
  };

  const back = (from: From) => setRoute(from === "home" ? { name: "home" } : { name: "more" });
  // Кредиты, открытые из отчёта, возвращают в отчёт.
  const backFromDebts = (r: { from: From; viaReport?: boolean }) => (r.viaReport ? setRoute({ name: "report", from: r.from }) : back(r.from));

  // Вложенные экраны — системная кнопка «Назад».
  useEffect(() => {
    if (route.name === "list") return pushBackHandler(() => setRoute({ name: "lists" }));
    if (route.name === "admin") return pushBackHandler(() => setRoute({ name: "more" }));
    if (route.name === "debts") {
      const r = route;
      return pushBackHandler(() => backFromDebts(r));
    }
    if (route.name === "report") {
      const from = route.from;
      return pushBackHandler(() => back(from));
    }
    return undefined;
  }, [route]);

  // Панель разработчика и разделы взрослых — только тем, кому положено. Проверка — ещё до отрисовки:
  // иначе чужая страница успеет смонтироваться и отправить запрос (ребёнку — 400 от /api/dashboard).
  const forbidden =
    me != null && ((route.name === "admin" && !me.isDeveloper) || ((route.name === "ops" || route.name === "debts" || route.name === "report") && isChild));
  useEffect(() => {
    if (forbidden) setRoute({ name: "home" });
    if (adding && isChild) setAdding(null);
  }, [forbidden, isChild, adding]);

  const go = (r: Route) => {
    hapticSelect();
    setRoute(r);
    window.scrollTo(0, 0);
  };

  const tabs: { name: TabName; icon: string; label: string; route: Route }[] = isChild
    ? [
        { name: "home", icon: "💰", label: t("pocketTab"), route: { name: "home" } },
        { name: "lists", icon: "🛒", label: t("lists"), route: { name: "lists" } },
        { name: "more", icon: "⚙️", label: t("moreTab"), route: { name: "more" } },
      ]
    : [
        { name: "home", icon: "🏠", label: t("homeTab"), route: { name: "home" } },
        { name: "ops", icon: "📋", label: t("opsTab"), route: { name: "ops" } },
        { name: "lists", icon: "🛒", label: t("lists"), route: { name: "lists" } },
        { name: "more", icon: "⚙️", label: t("moreTab"), route: { name: "more" } },
      ];
  const activeTab: TabName | null =
    route.name === "list" ? "lists" : route.name === "admin" ? "more" : route.name === "debts" || route.name === "report" ? route.from : route.name;
  const nested = route.name === "list" || route.name === "admin" || route.name === "debts" || route.name === "report";

  return (
    <div className={nested ? "app" : "app with-tabs"}>
      <StatusBar />
      {status === "unauthorized" || forbidden ? null : !me && route.name !== "lists" && route.name !== "list" ? (
        // Роль ещё неизвестна (первый запуск) — не запрашиваем чужие для ребёнка разделы.
        <main className="dash">
          <SkeletonCard lines={3} />
          <SkeletonCard lines={4} />
        </main>
      ) : route.name === "home" ? (
        isChild ? (
          <PocketPage childId={me!.userId} lang={lang} />
        ) : (
          <HomePage
            lang={lang}
            onAdd={setAdding}
            onOpenOps={() => go({ name: "ops" })}
            onOpenReport={() => go({ name: "report", from: "home" })}
            onOpenDebts={() => go({ name: "debts", from: "home" })}
          />
        )
      ) : route.name === "ops" ? (
        <OperationsPage key={route.month ?? "current"} lang={lang} initialMonth={route.month} onAdd={() => setAdding("expense")} />
      ) : route.name === "lists" ? (
        <ListsPage onOpen={(id) => go({ name: "list", id })} />
      ) : route.name === "list" ? (
        <ListPage id={route.id} onBack={() => setRoute({ name: "lists" })} onOpen={(id) => go({ name: "list", id })} />
      ) : route.name === "more" ? (
        <MorePage
          lang={lang}
          initialSheet={route.open ?? null}
          onLang={changeLang}
          onAdmin={() => go({ name: "admin" })}
          onDebts={() => go({ name: "debts", from: "more" })}
          onReport={() => go({ name: "report", from: "more" })}
          onOpenList={(id) => go({ name: "list", id })}
        />
      ) : route.name === "debts" ? (
        <DebtsPage lang={lang} onBack={() => backFromDebts(route)} />
      ) : route.name === "report" ? (
        <ReportPage
          lang={lang}
          onBack={() => back(route.from)}
          onAdd={setAdding}
          onOpenOps={(month) => go({ name: "ops", month })}
          onOpenDebts={() => go({ name: "debts", from: route.from, viaReport: true })}
        />
      ) : (
        <AdminPage lang={lang} onBack={() => setRoute({ name: "more" })} />
      )}

      {!nested && status !== "unauthorized" && (
        <nav className="tabbar" aria-label="sections">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              className={activeTab === tab.name ? "tabbar-item active" : "tabbar-item"}
              aria-current={activeTab === tab.name ? "page" : undefined}
              onClick={() => go(tab.route)}
            >
              <span aria-hidden="true">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      )}

      {adding && <AddTxSheet lang={lang} initialType={adding} onClose={() => setAdding(null)} />}
      <ToastHost />
    </div>
  );
}
